import React, { SetStateAction, Dispatch, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs } from "../../Styles";
import { IoModal, AddIoModal, Overlay } from "..";
import { ButtonTypes, ListData_ValueTypes, SelectFieldOption_Values, TableStyles_Types } from "../../Definitions";
import { DeviceInfo, DynamicDataObk } from "../../Services";
import { ReactiveTable } from "../Tables/reactiveTableRenderer";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, noop, useReactTable, } from "@tanstack/react-table";
import { GenericRow, useColumnConstructor } from "../Tables/_Tables/useColumnConstructor";
import { keyBy, merge } from "lodash";

import { OptionsWithRequiredNumberId, useLcIoOptions } from "../../Containers/FullViewScreens/GlobalScreens/Settings/_Settings/useLcIoOptions";
import { valueIsSelectFieldOptionsValue } from "../../Containers/FullViewScreens/GlobalScreens/Settings/_Settings/typeGuards";
import NewButton, { NewButtonList } from "../BaseComponents/newButton";

interface ioTableProps {
  deviceInfo: DeviceInfo[];
  maxTableHeight?: string;
  showSystemRow?: boolean;
  maintainDeviceInfo?: (
    changed?: Partial<DynamicDataObk<any>> | Partial<DynamicDataObk<any[]>>,
    deleted?: Partial<DynamicDataObk<any>> | Partial<DynamicDataObk<any[]>>,
    created?: Partial<DynamicDataObk<any>> | Partial<DynamicDataObk<any[]>>
  ) => void;
}

type PartialDeviceInfo = Partial<DeviceInfo> & Pick<DeviceInfo, "device_id">;

export interface BoardData extends GenericRow {
  id: number;
  action: JSX.Element;
  name?: string;
  cpuId?: SelectFieldOption_Values;
  systemAssignment?: OptionsWithRequiredNumberId;
  lanControllerId?: OptionsWithRequiredNumberId;
  index: number;
}

const deviceInfoDataMap = (
  board: DeviceInfo,
  index: number,
  setSelectedBoard: Dispatch<SetStateAction<number | undefined>>,
  lanControllerOptions: OptionsWithRequiredNumberId[],
  systemOptions: OptionsWithRequiredNumberId[]
) => {
  const actionButton = (
    <NewButton
      content={board.name.toString()}
      buttonType={ButtonTypes.textButton}
      startEnhancer={
        <glyphs.ArrowSmooth fillColor={colors.greyDarkSecondary} />
      }
      onClick={() => setSelectedBoard(index)}
      overrides={{
        contentStyles: {
          color: colors.greyDarkPrimary,
          ...fonts.fontSize4,
          ...fonts.paragraph,
        },
        startEnhancerStyles: { marginRight: "15px" },
      }}
    />
  );

  return {
    action: actionButton,
    id: board.device_id,
    cpu_id: {id: board.cpu_id, label: board.cpu_id ?? ""},
    name: board.name,
    lan_controller_id: lanControllerOptions.find(
      ({ id }) => board.lan_controller_id === id
    ),
    // type: board.type,
    power_supply: board.power_supply,
    max_ac_ports: board.max_ac_ports,
    system_assignment: systemOptions.find(
      ({ id }) => id === board.system_assignment
    ),
    index,
  };
};

export default function IoTable({ deviceInfo, maxTableHeight, showSystemRow = true,  maintainDeviceInfo }: ioTableProps) {
  const { t } = useTranslation();
  const [editTable, setEditTable] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState<number | undefined>(
    undefined
  );
  const [addBoardModal, setAddBoardModal] = useState<boolean>(false);
  const { systemOptions, lanControllerOptions, deviceCpuOptions } =
    useLcIoOptions();
  const dirtRef = useRef<PartialDeviceInfo[]>([]);
  const [boardData, setBoardData] = useState<BoardData[]>(
    [...deviceInfo].map((board, index) => {
      return deviceInfoDataMap(
        board,
        index,
        setSelectedBoard,
        lanControllerOptions,
        systemOptions
      );
    })
  );

  useEffect(() => {
    setBoardData(
      [...deviceInfo].map((board, index) => {
        return deviceInfoDataMap(
          board,
          index,
          setSelectedBoard,
          lanControllerOptions,
          systemOptions
        );
      })
    );
  }, [deviceInfo, lanControllerOptions, systemOptions]);

  const _maxTableHeight = maxTableHeight ? maxTableHeight : "unset";

  const accessorData = useMemo(() => {
    const baseData = [
      {
        headerValue: t("devices.name", { defaultValue: "name" }),
        accessor: "action",
        type: ListData_ValueTypes.Button,
        width: "150px",
        staticColumn: true,
      },
      {
        headerValue: t("devices.cpuId", { defaultValue: "cpu id" }),
        accessor: "cpu_id",
        type: ListData_ValueTypes.CreatableList,
        options: deviceCpuOptions,
      },
      // { headerValue: t("devices.type", {defaultValue:"type"}), accessor: "type", type: ListData_ValueTypes.List },
      {
        headerValue: t("devices.connection", { defaultValue: "connection" }),
        accessor: "lan_controller_id",
        type: ListData_ValueTypes.CreatableList,
        options: lanControllerOptions,
      },
      {
        headerValue: t("devices.powerSupply", { defaultValue: "power supply" }),
        accessor: "power_supply",
        type: ListData_ValueTypes.Number,
      },
      {
        headerValue: t("devices.maxAcPorts", { defaultValue: "max ac ports" }),
        accessor: "max_ac_ports",
        type: ListData_ValueTypes.Number,
      },
    ];

    if (showSystemRow) {
      return [
        ...baseData,
        {
          headerValue: t("devices.system", { defaultValue: "system" }),
          accessor: "system_assignment",
          type: ListData_ValueTypes.List,
          options: systemOptions,
        },
      ];
    }

    return baseData;
  }, [deviceCpuOptions, lanControllerOptions, showSystemRow, systemOptions, t]);

  const columns = useColumnConstructor({
    accessorData,
    tableStyle: TableStyles_Types.primaryTable,
    canEdit: editTable,
  });

  const table = useReactTable({
    columns,
    data: boardData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnPinning: {
        left: ["action"],
      },
    },
    meta: {
      deleteRow: noop,
      updateData: (rowIndex, columnId, value, row) => {
        const index = row.original.index;
        if (index) {
          boardData[Number(index)][columnId] = value;
          setBoardData([...boardData]);
        }
        let updateValue = value;
        if (value === "") {
          updateValue = undefined;
        }
        if (valueIsSelectFieldOptionsValue(value, columnId)) {
          if (value.id) {
            updateValue = +value.id;
          }
          if (typeof value.id === "number" && isNaN(value.id)) {
            updateValue = NaN;
          }
          if (value.id === undefined || value.label === "") {
            updateValue = NaN;
          }
        }
        if ( (columnId === "power_supply" || columnId === "max_ac_ports") && value ) {
          updateValue = +value;
        }
        if (columnId === "cpu_id") {
          updateValue = (value as any as SelectFieldOption_Values).label;
          if (value && typeof value === "object" && "id" in value && typeof value.id === "number" && isNaN(value.id)) {
            updateValue = NaN
          }
          if (value && typeof value === "object" && "id" in value && "label" in value && value.id === undefined && value.label === "") {
            updateValue = NaN
          }
        }
        dirtRef.current = Object.values(
          merge({}, keyBy(dirtRef.current, "device_id"), keyBy([{ device_id: rowIndex, [columnId]: updateValue || (typeof updateValue === "number" && isNaN(updateValue)) ? updateValue : undefined, }], "device_id"))
        );
      },
    },
  });

  const reactiveTable = ReactiveTable({
    table,
    tableType: TableStyles_Types.primaryTable,
    emptyText: t("devices.noIoBoards", { defaultValue: "no i/o boards found" }),
    renderFooter: false,
    tableToggled: editTable,
    minRows: 16,
  });

  return (
    <>
      { editTable ? <Overlay beforeUnload onClickAlert/> : <></>}
      <div
        style={{
          height: "100%",
          maxHeight: _maxTableHeight,
          width: "100%",
          display: "grid",
          gridTemplateRows: "50px 1fr",
          boxSizing: "border-box",
          overflow: "hidden",
          zIndex: editTable ? 9999 : 0,
          position: "relative"
        }}
      >
        <div
          style={{
            gridArea: "1/1/2/2",
            backgroundColor: "transparent",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
            padding: "0px 20px",
          }}
        >
          <p
            style={{
              ...fonts.header,
              ...fonts.fontSize3,
              color: colors.greyLightTertiary,
              marginBottom: "5px",
            }}
          >
            {t("devices.ioBoards", { defaultValue: "i/o boards" })}
          </p>
          {!editTable ? (
            <NewButtonList
              contents={[
                t("buttons.add", { defaultValue: "add" }),
                t("buttons.edit", { defaultValue: "edit" }),
              ]}
              onClick={(i) => {
                if (i === 0) {
                  setAddBoardModal(true);
                }
                if (i === 1) {
                  setEditTable(true);
                }
              }}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[
                glyphs.FilterParameter({}),
                glyphs.Add({}),
                glyphs.Edit({}),
              ]}
            />
          ) : (
            <NewButtonList
              contents={[
                t("buttons.save", { defaultValue: "save" }),
                t("buttons.cancel", { defaultValue: "cancel" }),
                t("buttons.add", { defaultValue: "add" }),
                t("buttons.edit", { defaultValue: "edit" }),
              ]}
              onClick={(i) => {
                if (i === 0) {
                  if (maintainDeviceInfo) {
                    maintainDeviceInfo({ data: dirtRef.current });
                  }
                  setEditTable(false);
                  
                }
                if (i === 1) {
                  setEditTable(false);
                  
                  setBoardData(
                    [...deviceInfo].map((board, index) => {
                      return deviceInfoDataMap(
                        board,
                        index,
                        setSelectedBoard,
                        lanControllerOptions,
                        systemOptions
                      );
                    })
                  );

                  dirtRef.current = [];
                }
                if (i === 2) {
                }
                if (i === 3) {
                }
              }}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[
                glyphs.Check({}),
                glyphs.Close({}),
                glyphs.FilterParameter({}),
                glyphs.Add({}),
                glyphs.Edit({}),
              ]}
              disabled={[false, false, true, true, true]}
              overrides={(index) => ({
                baseStyles: {
                  backgroundColor:
                    index === 4 ? colors.greyPrimary : colors.greySecondary,
                },
                disabledStyles: {
                  opacity: index === 4 ? 1 : 0.5,
                },
              })}
            />
          )}
        </div>
        <div
          style={{
            gridArea: "2/1/3/2",
            height: "100%",
            boxSizing: "border-box",
            position: "relative",
            backgroundColor: colors.greyLightSecondary,
            border: "5px solid " + colors.greyPrimary,
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          {reactiveTable}
        </div>
      </div>

      {/* Add IO Modal */}
      {addBoardModal && (
        <AddIoModal
          isModalOpen={!!addBoardModal}
          closeModal={() => setAddBoardModal(false)}
          maintainDeviceInfo={maintainDeviceInfo}
        />
      )}

      {/* IO Modal */}
      {selectedBoard !== undefined && (
        <IoModal
          selectedBoard={boardData[selectedBoard]}
          onNextBoard={() =>
            setSelectedBoard((prevIndex) =>
              prevIndex !== undefined ? (prevIndex + 1) % boardData.length : 0
            )
          }
          onPreviousBoard={() =>
            setSelectedBoard((prevIndex) =>
              prevIndex !== undefined
                ? (prevIndex - 1 + boardData.length) % boardData.length
                : boardData.length - 1
            )
          }
          closeModal={() => setSelectedBoard(undefined)}
        />
      )}
    </>
  );
}
